import * as React from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import styled from "styled-components";
import { getIn } from "formik";
import { FGFormGroup, IFGFormGroupProps, useFGContext } from "nsitools-react";
import { standardModules, standardFormats } from "./quillConfigs";

const StyledQuillWrapper = styled.div<{
  $disabled?: boolean;
  $readonly?: boolean;
  $minHeight?: string;
}>`
  .ql-container {
    font-family: inherit;
    font-size: 14px;
    min-height: ${(props) =>
      props.$readonly ? "auto" : props.$minHeight || "120px"};
    background: ${(props) => (props.$readonly ? "transparent" : "#fff")};
  }

  .ql-editor {
    min-height: ${(props) => (props.$readonly ? "auto" : "100px")};
    padding: ${(props) => (props.$readonly ? "0" : "12px 15px")};
  }

  .ql-toolbar {
    display: ${(props) => (props.$readonly ? "none" : "block")};
    border-color: #ced4da;
    border-radius: 3px 3px 0 0;
    background: #f5f5f5;
  }

  .ql-container {
    border-color: #ced4da;
    border-radius: ${(props) => (props.$readonly ? "0" : "0 0 3px 3px")};
    ${(props) => props.$readonly && "border: none;"}
  }

  ${(props) =>
    props.$disabled &&
    `
    opacity: 0.6;
    pointer-events: none;
  `}

  &.intent-danger .ql-container {
    border-color: #cd4246;
  }
`;

const ReadOnlyContent = styled.div`
  padding: 5px 0;
  min-height: 20px;
  text-wrap: wrap;
  overflow-wrap: anywhere;

  p {
    margin: 0 0 0.5em 0;
  }
  ul,
  ol {
    margin: 0.5em 0;
    padding-left: 1.5em;
  }
  strong {
    font-weight: 600;
  }
`;

export interface IFGRichTextInputProps
  extends Omit<IFGFormGroupProps, "children"> {
  placeholder?: string;
  readonly?: boolean;
  disabled?: boolean;
  minHeight?: string;
  modules?: ReactQuill.ReactQuillProps["modules"];
  formats?: string[];
  onBlur?: (value: string) => void;
  onChange?: (value: string) => void;
}

export const FGRichTextInput: React.FunctionComponent<
  IFGRichTextInputProps
> = ({
  placeholder,
  readonly = false,
  disabled,
  minHeight = "120px",
  modules = standardModules,
  formats = standardFormats,
  onBlur: externalBlur,
  onChange: externalOnChange,
  ...formGroupProps
}) => {
  const fgContext = useFGContext();
  const { formik, editMode, disabled: globalDisabled } = fgContext;
  const quillRef = React.useRef<ReactQuill>(null);

  const handleChange = React.useCallback(
    (
      content: string,
      _delta: unknown,
      source: string,
      editor: ReactQuill.UnprivilegedEditor
    ) => {
      // Ignore programmatic changes (mount, value prop sync) — only react to user edits
      if (source !== "user") return;

      const name = formGroupProps.name;
      // Quill returns "<p><br></p>" for empty content
      const cleanContent = editor.getText().trim() === "" ? "" : content;

      if (formik.getFieldProps(name)) {
        formik.setFieldValue(name, cleanContent);
      }

      if (externalOnChange) {
        externalOnChange(cleanContent);
      }
    },
    [formik, formGroupProps.name, externalOnChange]
  );

  const handleBlur = React.useCallback(() => {
    const name = formGroupProps.name;
    formik.setFieldTouched(name, true);

    if (externalBlur) {
      const value = getIn(formik.values, name) || "";
      externalBlur(value);
    }
  }, [formik, formGroupProps.name, externalBlur]);

  return (
    <FGFormGroup {...formGroupProps}>
      {({ intent, name }) => {
        const value = getIn(formik.values, name) || "";
        const isDisabled = disabled || globalDisabled;
        const isReadOnly = !editMode || readonly;

        return (
          <StyledQuillWrapper
            $disabled={isDisabled}
            $readonly={isReadOnly}
            $minHeight={minHeight}
            className={intent === "danger" ? "intent-danger" : ""}
          >
            {isReadOnly ? (
              <ReadOnlyContent
                dangerouslySetInnerHTML={{ __html: value || "" }}
              />
            ) : (
              <ReactQuill
                ref={quillRef}
                theme="snow"
                value={value}
                onChange={handleChange}
                preserveWhitespace={true}
                onBlur={handleBlur}
                modules={modules}
                formats={formats}
                placeholder={placeholder}
                readOnly={isDisabled}
              />
            )}
          </StyledQuillWrapper>
        );
      }}
    </FGFormGroup>
  );
};

export default FGRichTextInput;
